export type StaffImportStep =
    | 'form'
    | 'analyze'
    | 'preview'
    | 'confirm'
    | 'complete';

export type StaffImportSource = 'csv' | 'website';

export type StaffImportLogLevel = 'CRITICAL' | 'ERROR' | 'WARNING' | 'INFO';

export type StaffImportLogEntry = {
    level: StaffImportLogLevel;
    message: string;
};

export type StaffImportRow = {
    firstName: string;
    middleName: string;
    lastName: string;
    role: string;
    email: string;
    employeeId: string;
    gender: string;
    street: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
    birthdate: string;
    phoneNumber: string;
    onlineLink: string;
};

export type StaffImportRoleOption = {
    value: string;
    label: string;
};

/** Built-in roles shown to owners on New Staff (excludes superuser). */
export const STAFF_IMPORT_BUILTIN_ROLE_OPTIONS: readonly StaffImportRoleOption[] =
    [
        { value: 'Limited Staff', label: 'Limited Staff' },
        { value: 'Staff', label: 'Staff' },
        { value: 'Manager', label: 'Manager' },
        { value: 'Owner', label: 'Owner' },
    ] as const;

export const STAFF_IMPORT_GRID_ROLE_OPTIONS =
    STAFF_IMPORT_BUILTIN_ROLE_OPTIONS.map((option) => option.value);

export type StaffImportGridRole =
    (typeof STAFF_IMPORT_GRID_ROLE_OPTIONS)[number];

const BUILTIN_ROLE_BY_KEY: Record<string, StaffImportGridRole> = {
    limited_staff: 'Limited Staff',
    staff: 'Staff',
    manager: 'Manager',
    owner: 'Owner',
};

export const normalizeRoleKey = (value: string): string =>
    value
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_');

export const isAllowedStaffImportRole = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
        return false;
    }
    return STAFF_IMPORT_GRID_ROLE_OPTIONS.includes(
        trimmed as StaffImportGridRole,
    );
};

export const normalizeStaffRole = (value: string): StaffImportGridRole | '' => {
    const trimmed = value.trim();
    if (!trimmed) {
        return '';
    }
    const key = normalizeRoleKey(trimmed);
    return BUILTIN_ROLE_BY_KEY[key] ?? '';
};

export type StaffImportGridRow = {
    id: string;
    firstName: string;
    middleName: string;
    lastName: string;
    staffRole: string;
    email: string;
    gender: string;
    streetAddress: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    birthday: string;
    primaryPhone: string;
    employeeId: string;
    onlineLink: string;
};

export type StaffImportGridColumnKey = Exclude<keyof StaffImportGridRow, 'id'>;

export type StaffImportGridValidation = {
    cellErrors: Partial<Record<StaffImportGridColumnKey, string>>;
    cellWarnings: Partial<Record<StaffImportGridColumnKey, string>>;
    rowErrors: string[];
    rowWarnings: string[];
};

const STAFF_IMPORT_COLUMN_LABELS: Record<StaffImportGridColumnKey, string> = {
    firstName: 'First Name',
    middleName: 'Middle Name',
    lastName: 'Last Name',
    staffRole: 'Staff Role',
    email: 'Email',
    gender: 'Gender',
    streetAddress: 'Street Address',
    city: 'City',
    state: 'State',
    zip: 'Zip',
    country: 'Country',
    birthday: 'Birthday',
    primaryPhone: 'Primary Phone',
    employeeId: 'Employee ID',
    onlineLink: 'Online video conference link',
};

export const STAFF_IMPORT_GRID_COLUMN_KEYS = Object.keys(
    STAFF_IMPORT_COLUMN_LABELS,
) as StaffImportGridColumnKey[];

type RequiredStaffImportField = 'firstName' | 'lastName' | 'staffRole';

const REQUIRED_STAFF_IMPORT_FIELDS: RequiredStaffImportField[] = [
    'firstName',
    'lastName',
    'staffRole',
];

const REQUIRED_FIELD_ERROR_MESSAGES: Record<RequiredStaffImportField, string> =
    {
        firstName:
            'Missing first name - add first name or staff will not be imported.',
        lastName:
            'missing last name - add last name or staff will not be imported.',
        staffRole:
            'Staff role invalid - Please select a valid staff role or staff will not be imported.',
    };

const STAFF_ROLE_INVALID_MESSAGE =
    'Staff role invalid - Please select a valid staff role or staff will not be imported.';

const EMAIL_INVALID_FORMAT_MESSAGE =
    'Invalid email format. Add valid email or staff will not be able to login.';

const EMAIL_MISSING_MESSAGE = 'Add email or staff will not be able to login.';

const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const hasValidPhoneDigits = (value: string) => {
    const digitCount = value.replace(/\D/g, '').length;
    return digitCount >= 7 && digitCount <= 15;
};

const BIRTHDATE_PATTERN = /^(\d{4})[-/](\d{2})[-/](\d{2})$/;

export const normalizeBirthdate = (value: string): string | null => {
    const match = value.trim().match(BIRTHDATE_PATTERN);
    if (!match) {
        return null;
    }
    const normalized = `${match[1]}-${match[2]}-${match[3]}`;
    const date = new Date(`${normalized}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return date.toISOString().startsWith(normalized) ? normalized : null;
};

export const validateStaffImportGridRow = (
    row: StaffImportGridRow,
): StaffImportGridValidation => {
    const cellErrors: Partial<Record<StaffImportGridColumnKey, string>> = {};
    const cellWarnings: Partial<Record<StaffImportGridColumnKey, string>> = {};
    const rowErrors: string[] = [];
    const rowWarnings: string[] = [];

    REQUIRED_STAFF_IMPORT_FIELDS.forEach((field) => {
        if (row[field].trim().length === 0) {
            cellErrors[field] = REQUIRED_FIELD_ERROR_MESSAGES[field];
        }
    });

    if (
        row.staffRole.trim().length > 0 &&
        !isAllowedStaffImportRole(row.staffRole)
    ) {
        cellErrors.staffRole = STAFF_ROLE_INVALID_MESSAGE;
    }

    if (row.email.trim().length === 0) {
        cellWarnings.email = EMAIL_MISSING_MESSAGE;
    } else if (!isValidEmail(row.email)) {
        cellErrors.email = EMAIL_INVALID_FORMAT_MESSAGE;
    }

    if (
        row.primaryPhone.trim().length > 0 &&
        !hasValidPhoneDigits(row.primaryPhone)
    ) {
        cellErrors.primaryPhone = 'Primary Phone must contain 7 to 15 digits.';
    }

    if (
        row.birthday.trim().length > 0 &&
        normalizeBirthdate(row.birthday) === null
    ) {
        cellErrors.birthday = 'Birthdate must use YYYY-MM-DD or YYYY/MM/DD.';
    }

    return {
        cellErrors,
        cellWarnings,
        rowErrors,
        rowWarnings,
    };
};

export type ValidationIssue = {
    rowNumber: number;
    field: string;
    code: string;
    message: string;
    value?: string | null;
};

export type ValidationReport = {
    rowCount: number;
    validRowCount: number;
    invalidRowCount: number;
    issues: ValidationIssue[];
};

export type ImportJobStatus = {
    jobId: string;
    status: string;
    updatedAt: string | null;
    rowCount: number | null;
    errorCount: number | null;
    error: string | null;
};

export type GeneratedImportJobTrigger = {
    type: string;
    eventId: string;
    sourceUrl: string;
};

export type GeneratedImportJobSummary = {
    jobId: string;
    status: string;
    currentStage: string | null;
    createdAt: string;
    updatedAt: string;
    rowCount: number | null;
    staffPageUrl: string | null;
    trigger: GeneratedImportJobTrigger | null;
};

export type LatestGeneratedImportJobResult = {
    job: GeneratedImportJobSummary | null;
};

export type AnalyzeResult = {
    jobId: string;
    rows: StaffImportRow[];
    validation: ValidationReport;
    existingBusinessEmails: string[];
};

export type ProcessResult = {
    status: 'success' | 'partial' | 'failed';
    importedCount: number;
    failedCount: number;
    fileName: string;
    results: ProcessRowResult[];
};

export type ProcessRowResult = {
    rowId: string;
    status: 'success' | 'failed';
    peopleId: string | null;
    error: string | null;
};

export const isFullImportSuccess = (result: ProcessResult): boolean =>
    result.status === 'success' && result.failedCount === 0;

export type StaffImportToastVariant = 'error' | 'success';

export type StaffImportToast = {
    variant: StaffImportToastVariant;
    title: string;
    message?: string;
};

export type StaffImportState = {
    step: StaffImportStep;
    selectedFile: File | null;
    uploadedFileName: string | null;
    importJobId: string | null;
    importSource: StaffImportSource | null;
    isUserInitiated: boolean;
    uploading: boolean;
    polling: boolean;
    pollTimedOut: boolean;
    processing: boolean;
    isDirty: boolean;
    csvInlineError: string | null;
    webCrawlInlineError: string | null;
    toast: StaffImportToast | null;
    analyzeResult: AnalyzeResult | null;
    completedAt: string | null;
    gridRows: StaffImportGridRow[];
    processResult: ProcessResult | null;
    showConfirmModal: boolean;
    isReadyForImport: boolean;
    staffPageUrl: string | null;
};

export type StaffImportAction =
    | { type: 'SET_SELECTED_FILE'; file: File | null }
    | { type: 'DISMISS_TOAST' }
    | {
          type: 'UPLOAD_START';
          importSource: StaffImportSource;
          isUserInitiated: boolean;
      }
    | {
          type: 'UPLOAD_SUCCESS';
          uploadedFileName: string;
          importJobId: string;
          importSource: StaffImportSource;
          isUserInitiated: boolean;
          staffPageUrl?: string | null;
      }
    | {
          type: 'UPLOAD_FAILURE';
          inlineError: string;
          importSource: StaffImportSource;
          toast: StaffImportToast;
      }
    | { type: 'POLL_START' }
    | { type: 'POLL_TIMEOUT'; inlineError: string; toast: StaffImportToast }
    | { type: 'POLL_FAILURE'; inlineError: string; toast: StaffImportToast }
    | {
          type: 'ANALYZE_SUCCESS';
          result: AnalyzeResult;
          completedAt: string | null;
      }
    | {
          type: 'ANALYZE_FAILURE';
          inlineError: string;
          importSource: StaffImportSource;
          toast: StaffImportToast;
      }
    | { type: 'CLEAR_WEB_CRAWL_INLINE_ERROR' }
    | { type: 'ADOPT_DISCARD' }
    | { type: 'SET_GRID_ROWS'; gridRows: StaffImportGridRow[] }
    | { type: 'RETRY_FAILED_ROWS'; gridRows: StaffImportGridRow[] }
    | { type: 'GO_TO_PREVIEW' }
    | { type: 'GO_TO_ANALYZE' }
    | { type: 'GO_TO_FORM' }
    | { type: 'SHOW_CONFIRM_MODAL' }
    | { type: 'HIDE_CONFIRM_MODAL' }
    | { type: 'PROCESS_START' }
    | {
          type: 'PROCESS_SUCCESS';
          result: ProcessResult;
      }
    | {
          type: 'PROCESS_FAILURE';
          toast: StaffImportToast;
      }
    | { type: 'RESET' };

export const DUPLICATE_EMAIL_IN_BUSINESS_MESSAGE =
    'Email is already in use for this business.';

export type StaffImportProgressState = Pick<
    StaffImportState,
    | 'step'
    | 'selectedFile'
    | 'uploadedFileName'
    | 'analyzeResult'
    | 'gridRows'
    | 'uploading'
    | 'polling'
    | 'processing'
>;

export type StaffImportGridState = Pick<StaffImportState, 'gridRows'>;

export const deriveHasExistingGridImport = (
    state: StaffImportGridState,
): boolean => state.gridRows.length > 0;

export const deriveHasUnsavedImportProgress = (
    state: StaffImportProgressState,
): boolean => {
    if (state.step === 'complete' || state.processing) {
        return false;
    }

    return (
        state.selectedFile != null ||
        state.uploadedFileName != null ||
        state.analyzeResult != null ||
        state.gridRows.length > 0 ||
        state.uploading ||
        state.polling
    );
};

export const deriveGridReadyForImport = (
    gridRows: StaffImportGridRow[],
    existingBusinessEmails: string[] = [],
): boolean => {
    if (gridRows.length === 0) {
        return false;
    }

    const existingEmails = new Set(
        existingBusinessEmails.map((email) => email.trim().toLowerCase()),
    );
    const normalizedEmails = gridRows
        .map((row) => row.email.trim().toLowerCase())
        .filter(Boolean);
    const uniqueEmails = new Set(normalizedEmails);
    if (uniqueEmails.size !== normalizedEmails.length) {
        return false;
    }

    if (normalizedEmails.some((email) => email && existingEmails.has(email))) {
        return false;
    }

    return gridRows.every((row) => {
        const validation = validateStaffImportGridRow(row);
        return (
            validation.rowErrors.length === 0 &&
            Object.keys(validation.cellErrors).length === 0
        );
    });
};
